{/* DRAFT — owner must have legal review before launch */}
import { ArrowLeft } from 'lucide-react';

interface PrivacyPageProps {
  onNavigate: (page: string) => void;
}

const LAST_UPDATED = 'June 2026';

/**
 * Privacy Policy — DRAFT. Standard SaaS template populated with Karyva's real
 * data practices: data in PostgreSQL on our own infrastructure, three
 * subprocessors (Stripe, Resend, an AI service provider — provider-neutral per
 * CLAUDE.md), and GDPR export/deletion that already exist in-product. Bracketed
 * placeholders must be completed and the document reviewed by a lawyer before
 * launch.
 */
export function PrivacyPage({ onNavigate }: PrivacyPageProps) {
  return (
    <div className="bg-white">
      <section className="pt-16 pb-10 px-6 bg-gradient-to-b from-[#f8f9fa] to-white">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => onNavigate('home')}
            className="inline-flex items-center gap-1.5 text-sm text-[#556B7C] hover:text-[#2A3B4C] transition-colors mb-6"
          >
            <ArrowLeft size={15} /> Back to home
          </button>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#2A3B4C] mb-2">Privacy Policy</h1>
          <p className="text-[#556B7C] text-sm">Last updated: {LAST_UPDATED}</p>
          <div className="mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            Draft for review. This policy is pending legal review and may change before launch.
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto space-y-8 text-[#3a4a59] text-sm leading-relaxed">

          <Clause n="1" title="Who we are">
            This Privacy Policy explains how [Company legal name] ("Karyva", "we", "us")
            collects, uses, and protects personal data when you use the Karyva platform at
            app.karyva.com and our website (the "Service"). For data you upload about your
            own projects and team, you are the controller and we act as your processor; for
            account and billing data, we are the controller.
          </Clause>

          <Clause n="2" title="Data we collect">
            <span className="block mb-2">We collect:</span>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account data</strong> — name, email, organisation, and password (stored only as a salted hash).</li>
              <li><strong>Content you create</strong> — projects, tasks, comments, files, and other data you enter into the Service.</li>
              <li><strong>Billing data</strong> — handled by Stripe; we store a customer/subscription reference, not your full card number.</li>
              <li><strong>Usage &amp; device data</strong> — log data such as IP address and actions taken, used for security and to operate the Service.</li>
            </ul>
          </Clause>

          <Clause n="3" title="How we use your data">
            We use personal data to provide and secure the Service, authenticate users,
            process payments, send transactional email (for example, invitations and
            password resets), provide optional AI assistant features when you use them,
            respond to support requests, and meet legal obligations. We do not sell your
            personal data, and we do not use third-party advertising trackers.
          </Clause>

          <Clause n="4" title="Legal bases (GDPR)">
            Where GDPR applies, we process personal data on the bases of contract
            (to deliver the Service you signed up for), legitimate interests (to secure and
            improve the Service), consent (where required, e.g. for optional features), and
            legal obligation.
          </Clause>

          <Clause n="5" title="Subprocessors &amp; sharing">
            <span className="block mb-2">We share data only with the subprocessors needed to run the Service:</span>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Stripe</strong> — payment processing and subscription billing.</li>
              <li><strong>Resend</strong> — delivery of transactional email.</li>
              <li><strong>AI service provider</strong> — powers optional in-product AI assistant and insight features; data is sent only when you use those features.</li>
            </ul>
            <span className="block mt-2">
              Each subprocessor processes data solely to perform its function. We may also
              disclose data where required by law.
            </span>
          </Clause>

          <Clause n="6" title="Where your data is stored &amp; how we protect it">
            Customer data is stored in a PostgreSQL database on infrastructure we operate
            and control. We apply a zero-trust, multi-tenant isolation model so each
            organisation's data is segregated, and sensitive fields are encrypted at rest.
            Access is restricted and audited. No method of transmission or storage is 100%
            secure, but we work to protect your data using industry-standard safeguards.
          </Clause>

          <Clause n="7" title="Cookies">
            We use strictly necessary cookies to keep you signed in and to operate the
            Service securely (for example, session and authentication cookies). We do not
            use advertising or cross-site tracking cookies.
          </Clause>

          <Clause n="8" title="Your rights">
            Subject to applicable law, you have the right to access, correct, export, and
            delete your personal data, and to object to or restrict certain processing.
            Data <strong>export</strong> and <strong>deletion</strong> are available
            directly inside the product. To exercise other rights, contact us at the
            address below. You may also lodge a complaint with your local data protection
            authority.
          </Clause>

          <Clause n="9" title="Data retention">
            We retain Customer Data for as long as your account is active. After account
            closure or deletion, we delete or anonymise personal data within a reasonable
            period, except where we must retain it to comply with legal obligations.
          </Clause>

          <Clause n="10" title="International transfers">
            Where data is transferred across borders, we rely on appropriate safeguards
            (such as Standard Contractual Clauses) as required by applicable law.
          </Clause>

          <Clause n="11" title="Children's privacy">
            The Service is not directed to children under 16, and we do not knowingly
            collect their personal data.
          </Clause>

          <Clause n="12" title="Changes to this policy">
            We may update this policy from time to time. Material changes will be
            communicated by email or an in-product notice, and the "last updated" date
            above will change.
          </Clause>

          <Clause n="13" title="Contact">
            Questions or requests about your data? Email{' '}
            <a href="mailto:privacy@karyva.com" className="text-[#DAA520] hover:underline">privacy@karyva.com</a>.
            See also our{' '}
            <button onClick={() => onNavigate('terms')} className="text-[#DAA520] hover:underline">Terms of Service</button>.
          </Clause>

        </div>
      </section>
    </div>
  );
}

function Clause({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-base font-bold text-[#2A3B4C] mb-2">{n}. {title}</h2>
      <div>{children}</div>
    </div>
  );
}
